import React, { useEffect, useState, useRef, useCallback } from "react";
import { Loader2, Sparkles, RefreshCw, AlertCircle } from "lucide-react";
import { requestOrderSummary, getOrderSummaryStatus } from "../services/orderSummaryApi";

// ── Session-level cache ──────────────────────────────────────────────
// Keyed by orderId → { summary, summaries, orderDate, status, failureReason }
// Survives tab switches and re-renders but resets on full page reload.
const _summaryCache = new Map();

/**
 * LatestOrderSummaryCard — compact light-theme card that automatically
 * summarises the most recent court order. Rendered inside the overview
 * "Hearing Summary" panel in the new UI.
 *
 * Props:
 *   orders       Array   Raw order objects from caseData (first = latest)
 *   courtType    string  "hc" | "dc" | "sc" | "rera" | "nclt"
 *   caseNumber   string  Human-readable case number
 */
function LatestOrderSummaryCard({ orders, courtType, caseNumber }) {
  const [status, setStatus]             = useState("idle");   // idle | loading | completed | failed | empty
  const [summary, setSummary]           = useState(null);
  const [summaries, setSummaries]       = useState(null);
  const [orderDate, setOrderDate]       = useState(null);
  const [failureReason, setFailureReason] = useState(null);
  const pollRef    = useRef(null);
  const mountedRef = useRef(true);
  const triggeredRef = useRef(null); // tracks orderId we already triggered

  // ── Helpers ──────────────────────────────────────────────────────────

  /** Build the same stable orderId string that handleAiSummary uses. */
  const buildOrderId = useCallback((order) => {
    const date =
      order.orderDate || order.order_date || order.date ||
      order.listing_date || order["Order Date"] || order["Date"] || "unknown";
    return `${caseNumber}_${courtType}_${date}`.replace(/[^a-zA-Z0-9_-]/g, "_");
  }, [caseNumber, courtType]);

  /** Extract the human-readable date from an order object. */
  const extractDate = (order) =>
    order.orderDate || order.order_date || order.date ||
    order.listing_date || order["Order Date"] || order["Date"] || null;

  /** Extract PDF URLs from an order depending on court type. */
  const extractUrls = useCallback((order) => {
    if (courtType === "rera") {
      return { orderLink: order.url || null, pdfProxyUrl: null, pdfStorageUrl: null };
    }
    if (courtType === "nclt") {
      return { orderLink: order.url || null, pdfProxyUrl: null, pdfStorageUrl: null };
    }
    if (courtType === "sc") {
      return { orderLink: order.url || order.documentUrl || null, pdfProxyUrl: null, pdfStorageUrl: null };
    }
    // HC / DC
    return {
      orderLink: order.orderLink || order.order_link || order.url || null,
      pdfProxyUrl: order.pdfProxyUrl || order.pdfProxy || null,
      pdfStorageUrl: order.pdfStorageUrl || null,
    };
  }, [courtType]);

  // ── Cleanup ──────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, []);

  // ── Polling ──────────────────────────────────────────────────────────
  const startPolling = useCallback((jid, oid, date) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const res = await getOrderSummaryStatus(jid);
        if (!mountedRef.current) return;

        if (res.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          const firstSummary =
            res.summaries && res.summaries.length > 0
              ? res.summaries[0]?.summary || res.summary
              : res.summary;
          setSummary(firstSummary);
          if (res.summaries) setSummaries(res.summaries);
          setStatus("completed");
          _summaryCache.set(oid, { summary: firstSummary, summaries: res.summaries, orderDate: date, status: "completed" });
        } else if (res.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          const reason = res.failureReason || "unknown";
          setFailureReason(reason);
          setStatus("failed");
          _summaryCache.set(oid, { summary: null, orderDate: date, status: "failed", failureReason: reason });
        }
        // else keep polling
      } catch {
        // transient error — keep polling
      }
    }, 2000);
  }, []);

  // ── Main trigger ─────────────────────────────────────────────────────
  const triggerSummary = useCallback((order, oid) => {
    const date = extractDate(order);
    setOrderDate(date);
    setStatus("loading");
    setSummary(null);
    setSummaries(null);
    setFailureReason(null);

    const urls = extractUrls(order);

    (async () => {
      try {
        const res = await requestOrderSummary({
          orderId: oid,
          orderLink: urls.orderLink || undefined,
          pdfProxyUrl: urls.pdfProxyUrl || undefined,
          pdfStorageUrl: urls.pdfStorageUrl || undefined,
        });

        if (!mountedRef.current) return;

        if (res.status === "completed" && res.summary) {
          const firstSummary =
            res.summaries && res.summaries.length > 0
              ? res.summaries[0]?.summary || res.summary
              : res.summary;
          setSummary(firstSummary);
          if (res.summaries) setSummaries(res.summaries);
          setStatus("completed");
          _summaryCache.set(oid, { summary: firstSummary, summaries: res.summaries, orderDate: date, status: "completed" });
        } else if (res.status === "failed") {
          const reason = res.failureReason || "unknown";
          setFailureReason(reason);
          setStatus("failed");
          _summaryCache.set(oid, { summary: null, orderDate: date, status: "failed", failureReason: reason });
        } else {
          startPolling(res.jobId, oid, date);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        console.error("LatestOrderSummaryCard request error:", err);
        setFailureReason("llm_failed");
        setStatus("failed");
      }
    })();
  }, [extractUrls, startPolling]);

  // ── Auto-trigger on mount / orders change ────────────────────────────
  useEffect(() => {
    if (!orders || orders.length === 0) {
      setStatus("empty");
      return;
    }

    // Pick the latest order (first in array — APIs return descending)
    const latestOrder = orders[0];
    const oid = buildOrderId(latestOrder);

    // Avoid duplicate trigger for the same order
    if (triggeredRef.current === oid) return;
    triggeredRef.current = oid;

    // Check session cache first
    const cached = _summaryCache.get(oid);
    if (cached) {
      setOrderDate(cached.orderDate);
      if (cached.status === "completed") {
        setSummary(cached.summary);
        if (cached.summaries) setSummaries(cached.summaries);
        setStatus("completed");
      } else if (cached.status === "failed") {
        setFailureReason(cached.failureReason);
        setStatus("failed");
      }
      return;
    }

    triggerSummary(latestOrder, oid);

    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [orders, buildOrderId, triggerSummary]);

  // ── Retry handler ────────────────────────────────────────────────────
  const handleRetry = () => {
    if (!orders || orders.length === 0) return;
    const latestOrder = orders[0];
    const oid = buildOrderId(latestOrder);
    _summaryCache.delete(oid);
    triggeredRef.current = null; // allow re-trigger
    triggerSummary(latestOrder, oid);
  };

  // ── Render (light theme, sits inside a white cd-panel) ───────────────

  if (status === "empty") return null;

  return (
    <div style={{ marginTop: 12 }}>
      <style>{`
        @keyframes latestSummaryFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmerPulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 1; }
        }
      `}</style>

      <div
        className="rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50/60 overflow-hidden"
        style={{ animation: "latestSummaryFadeIn 0.35s ease-out" }}
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-purple-100">
          <div className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[#060125] font-semibold text-[13px] leading-tight">Latest Order Summary</h3>
            {orderDate && status !== "loading" && (
              <p className="text-gray-500 text-[11px] mt-0.5">Order dated {orderDate}</p>
            )}
          </div>
          {status === "failed" && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1 px-2.5 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 border border-purple-200 text-xs rounded-lg transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Retry
            </button>
          )}
        </div>

        {/* ── Body ───────────────────────────────────────────────── */}
        <div className="px-4 py-3 max-h-52 overflow-y-auto">
          {/* Loading */}
          {status === "loading" && (
            <div className="flex items-center gap-3">
              <Loader2 className="w-4 h-4 text-purple-500 animate-spin shrink-0" />
              <div>
                <p className="text-gray-600 text-[13px]" style={{ animation: "shimmerPulse 2s ease-in-out infinite" }}>
                  Analyzing latest court order...
                </p>
                <p className="text-gray-400 text-[11px] mt-0.5">This may take a few seconds</p>
              </div>
            </div>
          )}

          {/* Completed — show multi-segment or single summary */}
          {status === "completed" && summary && (
            <div>
              {summaries && summaries.length > 1 ? (
                <div className="flex flex-col gap-2.5">
                  <p className="text-[11px] text-gray-500">{summaries.length} orders detected in this document</p>
                  {[...summaries]
                    .sort((a, b) => a.order_index - b.order_index)
                    .map((s) => (
                      <div key={s.order_index} className={`rounded-lg p-2.5 border ${s.is_error ? "bg-red-50 border-red-200" : "bg-white/70 border-purple-100"}`}>
                        <h4 className={`${s.is_error ? "text-red-600" : "text-gray-700"} font-medium text-[11px] mb-1`}>
                          {s.case_no === "UNKNOWN" ||
                          (s.case_no.startsWith("ORDER_") && s.case_no.includes("_UNRESOLVED"))
                            ? `Order ${s.order_index + 1}`
                            : s.case_no}
                        </h4>
                        <p className={`${s.is_error ? "text-red-500/90" : "text-gray-700"} text-[13px] leading-relaxed`}>
                          {s.summary}
                        </p>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-700 text-[13px] leading-relaxed whitespace-pre-line">{summary}</p>
              )}
            </div>
          )}

          {/* Failed — PDF unavailable */}
          {status === "failed" && failureReason === "pdf_unavailable" && (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                <span className="text-sm">📄</span>
              </div>
              <p className="text-gray-500 text-[13px]">
                Unable to generate summary. The court server may be temporarily unavailable.
              </p>
            </div>
          )}

          {/* Failed — extraction */}
          {status === "failed" && failureReason === "extraction_failed" && (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-yellow-50 flex items-center justify-center shrink-0">
                <span className="text-sm">🔍</span>
              </div>
              <p className="text-gray-500 text-[13px]">
                Summary could not be generated. The order may be a scanned image document.
              </p>
            </div>
          )}

          {/* Failed — generic / LLM */}
          {status === "failed" && failureReason !== "pdf_unavailable" && failureReason !== "extraction_failed" && (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                <AlertCircle className="w-3.5 h-3.5 text-red-500" />
              </div>
              <p className="text-gray-500 text-[13px]">
                Summary could not be generated. Please try again later.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LatestOrderSummaryCard;
